import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import formActions from "../../../Actions/formActions";
import compensationActions from "../../../Actions/compensationActions";
import forms from "../../../Constants/forms";

import StandardModal, { ModalBody, ModalFooter } from "../../Fundamentals/Modal/StandardModal";
import CompensationForm from "../Compensation/CompensationForm";
import AlertSmall from "../../AlertSmall";

import useModal from "../../../Hooks/useModal";
import { useTranslation } from "react-i18next";
import system from "../../../Constants/system";
import { CompensationRestrictionReason } from "../../../Constants/Compensations/compensationRestrictionReasons";

export default function CompensationFormModal({ product }) {
    const dispatch = useDispatch();
    const { closeModal } = useModal();
    const { t } = useTranslation();

    const compensationForm = useSelector(state => state.FormReducer.forms[forms.compensationFormData]);
    const customer = useSelector(state => state.CustomerReducer.customer);
    const isLoading = useSelector((state) => state.CommonReducer.dataLoading.compensationForm);
    const isSumLoading = useSelector((state) => state.CommonReducer.dataLoading.compensationSum);

    const requireApproval = useMemo(() => !compensationForm?.data?.contactCustomer && product.sourceSystem === system.Voss, [compensationForm, product]);
    const contactCustomer = useMemo(() => compensationForm?.data?.contactCustomer, [compensationForm]);

    useEffect(() => {
        dispatch(compensationActions.beginCompensationSendForm(customer, product));

        return () => {
            dispatch(formActions.cancelFormEditing(forms.compensationFormData))
        };
    }, []);

    useEffect(() => {
        const { months, customerNumber } = compensationForm?.data || {};
        if (months) {
            dispatch(compensationActions.getCompensationSum(product, customerNumber, months, (sum) => {
                dispatch(formActions.changeMultiFormField(compensationForm, "sum", sum));
            }));
        }
    }, [compensationForm?.data?.months]);

    useEffect(() => {
        if (!compensationForm) return;
        const errorFlag = compensationForm.data?.contactCustomer ? {} : { months: t("compensationformmodal.pleaseselectmonths") };
        dispatch(formActions.updateFieldErrorFlags(compensationForm, 'months', errorFlag));
    }, [compensationForm?.data?.contactCustomer]);

    const handleFieldChange = (form, value, field) => {
        dispatch(formActions.changeMultiFormField(form, field, value));
    };

    const handleSave = () => {
        dispatch(formActions.submitForm(compensationForm, () => {
            dispatch(compensationActions.sendCompensation(compensationForm, () => {
                closeModal();
            }));
        }));
    };

    const renderAlert = () => {
        const { restrictionReason } = compensationForm.data.restrictions;

        const restrictionMessages = {
            [CompensationRestrictionReason.IsAoRestricted]: t("compensationmodal.accountingofficemessage"),
            [CompensationRestrictionReason.IsTransactionService]: t("compensationmodal.transactionservicemessage")
        };

        const message = restrictionMessages[restrictionReason]
            ?? (requireApproval ? t("compensationmodal.requireapprovalmessage")
                : contactCustomer ? t("compensationmodal.contactcustomermessage")
                    : t("compensationmodal.avtalmessage"));

        return <AlertSmall alert={{ message: <p>{message}</p>, type: "info" }} />;
    };

    return (
        <StandardModal title={t("compensationformmodal.title", { productName: product.name })} isLoading={isLoading}>
            {compensationForm && <ModalBody>
                <div className="mb-24">{renderAlert()}</div>
                <CompensationForm
                    form={compensationForm}
                    onChange={(value, field) => handleFieldChange(compensationForm, value, field)}
                />
            </ModalBody>}
            <ModalFooter>
                <div className="w-100 d-flex justify-content-end gap-8">
                    <button type="button" disabled={isSumLoading} className="btn btn-primary btn-sm mw-100" onClick={handleSave}>
                        {requireApproval ? t("compensationformmodal.sendapprovalrequest") : t("common.submit")}
                    </button>
                    <button type="button" className="btn btn-sm" onClick={closeModal}>{t("common.cancel")}</button>
                </div>
            </ModalFooter>
        </StandardModal>
    );
}